import { useRefs } from '@/scripts/hooks';
import { useQueue } from '@/scripts/utils/queue';
import { safeJson } from '@/scripts/utils';
import type {
  SendParams,
  ChatMessagesLoadParams,
  RetryParams,
} from '../hooks/useChatMessages/types';
import {
  ChatMessageCategory,
  ChatMessageOwnerIdValues,
  ChatMessagePayloadType,
  ChatMessageStatus,
} from '../hooks/useChatMessages/types';
import { eachMessagesArgs, pushMessagesArgs } from '../hooks/useChatMessages/utils';
import { ChatCategory } from '../hooks/useChats/types';
import { ChatSocketControls, NoticeCode, PushType } from '../hooks/useChatSocket/types';
import type { ChatContextHooksFull, ChatContextHooksProps } from '../../context';
import type {
  ChatMessagesArgMessageIdPullParams,
  SendMessageToServerParams,
} from './messages';

export type UseChatSingleMessagesValueResult = ReturnType<
  typeof useChatSingleMessagesValue
>;

export function useChatSingleMessagesValue({ hooks }: ChatContextHooksProps) {
  const { singleEncryption, chatSocket, chats, chatMediaCall } = hooks;
  const [loading, setLoading] = useState(true);

  const { socket, event, loading: socketLoading } = chatSocket;

  const queue = useQueue();

  const sendSingleMessageToServer = useCallback(
    (params: SendMessageToServerParams) => {
      const {
        chatMessagesValue: { sendMessageToServer },
      } = hooks as ChatContextHooksFull;
      return sendMessageToServer({
        ...params,
        handlers: {
          ...params?.handlers,
          async encrypt(message) {
            const {
              sendParams: { chat },
            } = params;
            const ciphertext = await singleEncryption.encryptMessage(
              chat.receiver_id,
              message.payload!
            );
            return {
              ...message,
              payload: ciphertext,
            };
          },
        },
      });
    },
    [hooks, singleEncryption]
  );

  const sendSingleMessage = useCallback(
    async (params: SendParams) => {
      const {
        chatMessagesValue: { sendMessageCommon },
      } = hooks as ChatContextHooksFull;
      await sendMessageCommon(params, sendSingleMessageToServer);
    },
    [hooks, sendSingleMessageToServer]
  );

  const retrySingleMessage = useCallback(
    async (params: RetryParams) => {
      const {
        chatMessagesValue: { retryMessage },
      } = hooks as ChatContextHooksFull;
      return retryMessage(params, sendSingleMessageToServer);
    },
    [hooks, sendSingleMessageToServer]
  );

  const pullSingleMessages = useCallback(
    async (params: ChatMessagesArgMessageIdPullParams[]) => {
      const {
        chatMessagesValue: { pullMessagesCommon, messageArrivalReply },
        groupEncryption,
      } = hooks as ChatContextHooksFull;

      const { args, push } = pushMessagesArgs<ChatMessagesArgMessageIdPullParams>();

      const result = await pullMessagesCommon(ChatCategory.SINGLE, params, {
        pulled: async (pulledMessage, { chat_id }) => {
          const isSelf = pulledMessage.category === ChatMessageCategory.SEND;

          if (pulledMessage.payload_type === ChatMessagePayloadType.EXPIRE_DURATION) {
            pulledMessage.read_at = pulledMessage.create_at;
          }
          if (
            pulledMessage.payload_type ===
            ChatMessagePayloadType.GROUP_ENCRYPTION_WELCOME
          ) {
            await groupEncryption.handleJoinGroup(pulledMessage);
          } else if (
            !ChatMessageOwnerIdValues.includes(pulledMessage.owner_id as any) &&
            !chatMediaCall.isMediaPayloadTypeCall(pulledMessage.payload_type!) &&
            !chatMediaCall.isMediaPayloadTypeStatus(pulledMessage.status!)
          ) {
            // decrypt
            if (pulledMessage.payload_type === ChatMessagePayloadType.ENCRYPTED_TEXT) {
              pulledMessage.payload =
                await singleEncryption.decryptMessage(pulledMessage);
            }
            if (!isSelf) {
              push(pulledMessage, chat_id);
            }
          }

          return pulledMessage;
        },
      });

      await Promise.all(
        args.map(async (item) => {
          await messageArrivalReply({
            chatId: item.chat_id,
            messageIdList: item.messages.map((m) => m.message_id),
          });
        })
      );

      return result;
    },
    [hooks, chatMediaCall, singleEncryption]
  );

  const pullSingle = useCallback(
    async (serverSequence: number) =>
      queue.push(() => {
        const {
          singleChatsValue: { loadSingleChatByChatId },
          chatMessagesValue: { updateLastReadMessages, pullCommon },
          chatGroupMessagesValue: { updateGroupInfo, addRemoveMessages },
          chatMediaGroupValue: { mediaStatusUpdateGroup },
        } = hooks as ChatContextHooksFull;

        return pullCommon(ChatCategory.SINGLE, serverSequence, {
          loadChats: loadSingleChatByChatId,
          pullMessages: pullSingleMessages,
          async updateMessagesBefore(updateList) {
            await eachMessagesArgs(updateList, (msg) => {
              if (msg.status === ChatMessageStatus.READ) {
                Object.assign(msg, {
                  read_at: msg.update_at,
                });
              }
            });

            // Update for single message status
            await updateLastReadMessages(updateList);

            return updateList;
          },
          async systemMessagesHandler(result) {
            await Promise.all([
              updateGroupInfo(result.pulledList),
              addRemoveMessages(result.pulledList),
              mediaStatusUpdateGroup(result.pulledList),
            ]);
          },
        });
      }),
    [queue, hooks, pullSingleMessages]
  );

  const loadSingleMessages = useCallback(
    (params: ChatMessagesLoadParams) => {
      const {
        chatMessagesValue: { loadMessages },
      } = hooks as ChatContextHooksFull;
      return loadMessages(
        params,
        async (arg) => (await pullSingleMessages([arg])).list[0]
      );
    },
    [hooks, pullSingleMessages]
  );

  const refs = useRefs({
    chats,
    pullSingle,
    pullSingleMessages,
    hooks: hooks as ChatContextHooksFull,
  });

  useLayoutEffect(() => {
    if (socket) {
      setLoading(true);
      refs.current.pullSingle(Infinity).finally(() => {
        setLoading(false);
      });

      const fns = [
        event.onMessage(
          ({ body }) => {
            refs.current.pullSingle(body.sequence);
          },
          {
            control: ChatSocketControls.NOTICE_MSG,
            code: NoticeCode.NEW_MSG,
            msgType: ChatCategory.SINGLE,
          }
        ),
        event.onMessage(
          ({ body }) => {
            const { chat_id } = safeJson(body.content, { chat_id: '' });
            if (chat_id) {
              refs.current.hooks.singleChatsValue.syncSingleChat(chat_id);
            }
          },
          {
            control: ChatSocketControls.DIRECT_PUSH,
            pushType: PushType.EXPIRE_DURATION_NOTICE_SINGLE,
          }
        ),
        (() => {
          const timer = setInterval(
            () => {
              refs.current.pullSingle(Infinity);
            },
            2 * 60 * 1000
          );
          return () => {
            clearInterval(timer);
          };
        })(),
      ];

      return () => {
        fns.forEach((fn) => fn());
      };
    } else {
      setLoading(socketLoading);
    }
  }, [refs, socket, event, socketLoading]);

  return useMemo(
    () => ({
      loading,
      sendSingleMessageToServer,
      sendSingleMessage,
      retrySingleMessage,
      pullSingleMessages,
      pullSingle,
      loadSingleMessages,
    }),
    [
      loading,
      loadSingleMessages,
      pullSingle,
      pullSingleMessages,
      retrySingleMessage,
      sendSingleMessage,
      sendSingleMessageToServer,
    ]
  );
}
